import { ReactElement, PropsWithChildren } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { colorFillColorWithState, fontTypo } from 'mt-react-theme-manager';
import { securityFaq } from '../../../configs/links.json';
import SproutLogo from '../../../components/SproutLogo';
import DataAccessScopes from '../DataAccessScopes';
import ContentSection from './ContentSection';
import ChangeEmailButton from './ChangeEmailButton';
import TosAndPpLinks from '../../../components/TosAndPpLinks';
import { RequiredPolicyLinks } from '../../../components/RequiredPolicies/RequiredPolicyLinks';
import { OAuthConsentNotice } from '../../../components/OAuthConsentNotice';

import './styles.scss';

type FormContentProps = PropsWithChildren<{
  email: string;
  clientId: string;
  clientName: string;
  oauthConsentTosLink: string;
  oauthConsentPpLink: string;
  oauthConsentNotice?: string;
  onTriggerChangeEmail: () => void;
  emailSetByGuest: boolean;
}>;

export default function FormContent({
  email,
  clientName,
  onTriggerChangeEmail,
  oauthConsentTosLink,
  oauthConsentPpLink,
  oauthConsentNotice,
  emailSetByGuest
}: FormContentProps): ReactElement {
  const { t } = useTranslation();

  const linkColor = colorFillColorWithState('primary');

  return (
    <div className={`fast-onboard-form-ja ${fontTypo('body1')}`}>
      <div className="logo-header">
        <SproutLogo />
      </div>
      <p className="description">
        <Trans
          i18nKey="FastOnboardFormJP.description"
          tOptions={{ clientName }}
        >
          <span className={fontTypo('headline')} />
        </Trans>
      </p>

      {oauthConsentNotice && <OAuthConsentNotice notice={oauthConsentNotice} />}

      <div className="consent-sections">
        <ContentSection
          headerNum={1}
          headerText={t('FastOnboardFormJP.content1.header')}
        >
          <div className="content1-description">
            {emailSetByGuest
              ? t('FastOnboardFormJP.content1.emailFromGuest')
              : t('FastOnboardFormJP.content1.emailFromClient', { clientName })}
          </div>
          <ChangeEmailButton email={email} onClick={onTriggerChangeEmail} />
          <a
            className={`have-an-account-link ${fontTypo('caption1')}`}
            href={`/oauth/authorize${window.location.search}`}
          >
            {t('FastOnboardForm.haveAnAccount')}
          </a>
          <Trans
            i18nKey={'FastOnboardFormJP.content1.description2'}
            components={{
              RequiredPolicyLinks: <RequiredPolicyLinks jaSeparator="と" />,
              SecurityFaqLink: (
                <a
                  href={securityFaq.JP}
                  target="_blank"
                  className={linkColor}
                />
              )
            }}
          />
        </ContentSection>
        <ContentSection
          headerNum={2}
          headerText={t('FastOnboardFormJP.content2.header', { clientName })}
        >
          <DataAccessScopes />
          <div className={`duration-area ${fontTypo('caption1')}`}>
            {t('FastOnboardFormJP.content2.duration', { clientName })}
          </div>
        </ContentSection>
      </div>
      <p className="description2">
        <Trans
          i18nKey={'FastOnboardFormJP.content2.description'}
          tOptions={{ clientName }}
          components={[
            <TosAndPpLinks
              tos={oauthConsentTosLink}
              pp={oauthConsentPpLink}
              linkClassName={colorFillColorWithState('primary')}
            />
          ]}
        />
      </p>
    </div>
  );
}
